import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { capacitorPreferencesStorage } from "@/lib/preferences-storage";
import type {
  Appetite,
  Diet,
  Expertise,
  HouseholdProfile,
  LocalHouseholdProfile,
} from "@/types/profile";
import { ONBOARDING_STEPS, type OnboardingStepId } from "./constants";

interface OnboardingState extends LocalHouseholdProfile {
  step: OnboardingStepId;
  setSize: (size: number) => void;
  setDiet: (diet: Diet) => void;
  toggleAllergy: (id: string) => void;
  toggleEquipment: (id: string) => void;
  setExpertise: (level: Expertise) => void;
  setAppetite: (a: Appetite) => void;
  setBatchSchedule: (startDay: number, numDays: number) => void;
  setCookingDay: (day: number) => void;
  setMeatDaysPerWeek: (days: number | null) => void;
  addFoodExclusion: (item: string) => void;
  removeFoodExclusion: (item: string) => void;
  goNext: () => void;
  goBack: () => void;
  reset: () => void;
}

const DEFAULTS: LocalHouseholdProfile = {
  size: 2,
  diet: "omnivore",
  allergies: [],
  equipment: ["stovetop", "oven"],
  expertise: "normal",
  appetite: "normal",
  foodExclusions: [],
  batch_start_day: 0,
  batch_num_days: 7,
  cooking_day: 6,
  meat_days_per_week: null,
};

/** Diets that have no concept of meat/fish — "meat-days" step is skipped. */
const MEATLESS_DIETS: Diet[] = ["vegetarian", "vegan"];

function neighbour(
  step: OnboardingStepId,
  delta: 1 | -1,
  diet: Diet,
): OnboardingStepId {
  let idx = ONBOARDING_STEPS.indexOf(step);
  // Traverse until we land on a valid step for this diet
  do {
    idx = Math.min(Math.max(idx + delta, 0), ONBOARDING_STEPS.length - 1);
  } while (
    ONBOARDING_STEPS[idx] === "meat-days" &&
    MEATLESS_DIETS.includes(diet)
  );
  return ONBOARDING_STEPS[idx];
}

export const useOnboarding = create<OnboardingState>()(
  persist(
    (set) => ({
      step: "size",
      ...DEFAULTS,

      setSize: (size) => set({ size }),
      setDiet: (diet) =>
        set((s) => ({
          diet,
          // Clear meat constraint when switching to a meatless diet.
          meat_days_per_week: MEATLESS_DIETS.includes(diet)
            ? null
            : s.meat_days_per_week,
        })),
      toggleAllergy: (id) =>
        set((s) => ({
          allergies: s.allergies.includes(id)
            ? s.allergies.filter((a) => a !== id)
            : [...s.allergies, id],
        })),
      toggleEquipment: (id) =>
        set((s) => ({
          equipment: s.equipment.includes(id)
            ? s.equipment.filter((e) => e !== id)
            : [...s.equipment, id],
        })),
      setExpertise: (expertise) => set({ expertise }),
      setAppetite: (appetite) => set({ appetite }),
      setBatchSchedule: (startDay, numDays) =>
        set({ batch_start_day: startDay, batch_num_days: numDays }),
      setCookingDay: (day) => set({ cooking_day: day }),
      setMeatDaysPerWeek: (days) => set({ meat_days_per_week: days }),
      addFoodExclusion: (item) =>
        set((s) => ({
          foodExclusions: s.foodExclusions.includes(item)
            ? s.foodExclusions
            : [...s.foodExclusions, item],
        })),
      removeFoodExclusion: (item) =>
        set((s) => ({
          foodExclusions: s.foodExclusions.filter((i) => i !== item),
        })),
      goNext: () => set((s) => ({ step: neighbour(s.step, 1, s.diet) })),
      goBack: () => set((s) => ({ step: neighbour(s.step, -1, s.diet) })),
      reset: () => set({ step: "size", ...DEFAULTS }),
    }),
    {
      name: "bc.onboarding",
      version: 3,
      storage: createJSONStorage(() => capacitorPreferencesStorage),
      migrate: (persisted, version) => {
        const state = persisted as Record<string, unknown>;
        if (version < 2) {
          state.appetite = "normal";
        }
        if (version < 3) {
          state.batch_start_day = 0;
          state.batch_num_days = 7;
          state.cooking_day = 6;
          state.meat_days_per_week = null;
        }
        return state as unknown as OnboardingState;
      },
    },
  ),
);

export function selectProfile(s: OnboardingState): LocalHouseholdProfile {
  return {
    size: s.size,
    diet: s.diet,
    allergies: s.allergies,
    equipment: s.equipment,
    expertise: s.expertise,
    appetite: s.appetite,
    foodExclusions: s.foodExclusions,
    batch_start_day: s.batch_start_day,
    batch_num_days: s.batch_num_days,
    cooking_day: s.cooking_day,
    meat_days_per_week: s.meat_days_per_week,
  };
}

export function selectHouseholdPayload(s: OnboardingState): HouseholdProfile {
  const { foodExclusions: _omit, ...rest } = selectProfile(s);
  return rest;
}
